import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { Button } from "src/components/ui/button";
import "jspdf-autotable";
import { FileText, Printer, Download, FileDown } from "lucide-react";

const ExportActions = ({ data, columns }) => {
  const exportPDF = async () => {
    try {
      // Dynamically import the required libraries
      const [jsPDFModule, html2canvasModule] = await Promise.all([
        import("jspdf"),
        import("html2canvas"),
      ]);

      const jsPDF = jsPDFModule.default;
      const html2canvas = html2canvasModule.default;

      // Create a temporary div to render our table
      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.left = "-9999px";
      tempDiv.style.top = "-9999px";

      // Check if we're in RTL mode
      const isRTL = "rtl";
      if (isRTL) {
        tempDiv.dir = "rtl";
      }

      // Create the table HTML
      tempDiv.innerHTML = `
      <html lang="ar" dir="rtl">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Team Asakeeb</title>
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            @media print {
              .no-print {
                display: none !important;
              }
              @page {
                margin: 1cm;
              }
            }
          </style>
        </head>
        <body class="bg-gray-50 text-black p-4 md:p-8">
          <div class="max-w-7xl mx-auto bg-white rounded-lg shadow-md p-6 md:p-8">
            <h1 class="text-2xl md:text-3xl font-bold text-center mb-6 text-gray-800">Team Asakeeb</h1>
            
            <div class="overflow-x-auto">
              <table class="w-full border-collapse mb-6">
                <thead>
                  <tr class="bg-primary-50">
                    ${columns
                      .map(
                        (col) =>
                          `<th class="px-4 py-3 text-right border-b-2 border-primary-200 font-semibold text-gray-800">${col.header}</th>`
                      )
                      .join("")}
                  </tr>
                </thead>
                <tbody>
                  ${data
                    .map(
                      (row, index) => `
                    <tr class="${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } text-gray-700">
                      ${columns
                        .map(
                          (col) =>
                            `<td class="px-4 py-3 text-right border-b border-gray-200">${
                              row[col.accessorKey] || ""
                            }</td>`
                        )
                        .join("")}
                    </tr>
                  `
                    )
                    .join("")}
                </tbody>
              </table>
            </div>
            
            <div class="text-center text-sm text-gray-500 mt-6">
              Generated on: ${new Date().toLocaleDateString("ar-EG")}
            </div>
          </div>
        </body>
      </html>
      `;

      document.body.appendChild(tempDiv);

      // Use html2canvas to capture the table as an image
      const canvas = await html2canvas(tempDiv, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      // Remove the temporary div
      document.body.removeChild(tempDiv);

      // Calculate the PDF dimensions based on the canvas
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");

      // Add image to PDF (the table)
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);

      // Save the PDF
      const filename = `table_export_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`;
      pdf.save(filename);
    } catch (error) {
      console.error("Failed to export PDF:", error);
      alert(
        "Failed to export PDF. Please make sure jspdf and html2canvas are installed."
      );
    }
  };

  const printTable = () => {
    const printWindow = window.open("", "", "height=600,width=800");

    // Add proper HTML structure with styling
    printWindow.document.write(`
   <html lang="ar" dir="rtl">
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Team Asakeeb</title>
    <script src="https://cdn.tailwindcss.com"></script>
    <script>
      tailwind.config = {
        theme: {
          extend: {
            colors: {
             primary: {
                50:  '#f5e7fe',
                100: '#edd4fe',
                200: '#dbb2fd',
                300: '#c084fc',
                400: '#a855f7',
                500: '#9333ea',
                600: '#7e22ce',
                700: '#6b21a8',
                800: '#581c87',
                900: '#471174', 
              }
            }
          }
        }
      }
    </script>
    <style>
      @media print {
        .no-print {
          display: none !important;
        }
        @page {
          margin: 1cm;
        }
      }
    </style>
  </head>
  <body class="bg-gray-50 text-gray-800 p-4 md:p-8">
    <div class="max-w-7xl mx-auto bg-white rounded-lg shadow-md p-6 md:p-8">
      <h1 class="text-2xl md:text-3xl font-bold text-center text-primary-700 mb-6">Team Asakeeb</h1>
      
      <div class="overflow-x-auto">
        <table class="w-full border-collapse mb-6">
          <thead>
            <tr class="bg-primary-50">
              ${columns
                .map(
                  (col) =>
                    `<th class="px-4 py-3 text-right border-b-2 border-primary-200 font-semibold text-primary-800">${col.header}</th>`
                )
                .join("")}
            </tr>
          </thead>
          <tbody>
            ${data
              .map(
                (row, index) => `
              <tr class="${
                index % 2 === 0 ? "bg-white" : "bg-gray-50"
              } hover:bg-primary-50 transition-colors duration-150">
                ${columns
                  .map(
                    (col) =>
                      `<td class="px-4 py-3 text-right border-b border-gray-200">${
                        row[col.accessorKey] || ""
                      }</td>`
                  )
                  .join("")}
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </div>
      
      <div class="text-center text-sm text-gray-500 mt-6">
        Generated on: ${new Date().toLocaleDateString("ar-EG")}
      </div>
      
      <div class="no-print flex justify-center gap-4 mt-8">
        <button onclick="window.print()" class="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-150 shadow-sm">
          طباعة
        </button>
        <button onclick="window.close()" class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors duration-150 shadow-sm">
          إغلاق
        </button>
      </div>
    </div>
  </body>
</html>
  `);

    printWindow.document.close();

    // Give the browser a moment to process the document before printing
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);
  };

  const exportExcel = () => {
    try {
      // Dynamically import xlsx
      import("xlsx")
        .then((XLSX) => {
          // Create a worksheet
          const worksheet = XLSX.utils.json_to_sheet(
            data.map((row) => {
              const rowData = {};
              columns.forEach((col) => {
                rowData[col.header] = row[col.accessorKey] || "";
              });
              return rowData;
            })
          );

          // Create a workbook and add the worksheet
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "Table Data");

          // Generate Excel file and trigger download
          const filename = `table_export_${new Date()
            .toISOString()
            .slice(0, 10)}.xlsx`;
          XLSX.writeFile(workbook, filename);
        })
        .catch((error) => {
          console.error("Failed to load xlsx library:", error);
          alert(
            "Failed to export Excel. Please make sure xlsx library is installed."
          );
        });
    } catch (error) {
      console.error("Failed to export Excel:", error);
      alert(
        "Failed to export Excel. Please make sure xlsx library is installed."
      );
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-9">
          <Download className="ml-2 h-4 w-4" />
          تصدير
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={exportPDF}>
          <FileText className="ml-2 h-4 w-4" />
          تصدير كـ PDF
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportExcel}>
          <FileDown className="ml-2 h-4 w-4" />
          تصدير كـ Excel
        </DropdownMenuItem>
        <DropdownMenuItem onClick={printTable}>
          <Printer className="ml-2 h-4 w-4" />
          طباعة
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportActions;
